import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Banknote,
  Receipt,
  FileText,
  Wallet,
  FileBarChart,
  Activity,
  LogOut,
  Menu,
  X,
  Settings,
  Search,
  Building2,
  UserCog,
  Package,
  CreditCard,
  HardDrive,
  Send,
  Calendar,
  Sprout,
  Megaphone,
  BookOpen,
  ClipboardList,
  ArrowRightLeft,
  GraduationCap,
  FolderKanban,
  Folder,
  Plus,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

import NoticePopup from './NoticePopup';
import RouteLoader from './RouteLoader';

const Layout = () => {
  const { user, logout, isAdmin, isAdminOnly } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notifCounts, setNotifCounts] = useState({ requisitions: 0 });
  // Track expanded state for grouped nav items (e.g., "Projects")
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_expanded_groups');
      return saved ? JSON.parse(saved) : { Projects: true };
    } catch {
      return { Projects: true };
    }
  });
  const toggleGroup = (name) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      try { localStorage.setItem('sidebar_expanded_groups', JSON.stringify(next)); } catch (_e) { /* ignore */ }
      return next;
    });
  };

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    // Set initial state
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Auto-sync: poll notification counts every 30 seconds
  const fetchNotifications = useCallback(async () => {
    try {
      const resp = await api.get('/notifications/counts');
      setNotifCounts(resp.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  // Re-fetch on route change (instant sync when navigating)
  useEffect(() => {
    if (user) fetchNotifications();
  }, [location.pathname, user, fetchNotifications]);

  // Dynamic Project list — fills the "Projects" sidebar group with names created in Project Master
  const [projects, setProjects] = useState([]);
  const fetchProjects = useCallback(async () => {
    try {
      const resp = await api.get('/projects');
      setProjects(Array.isArray(resp.data) ? resp.data : []);
    } catch (_e) { /* ignore — non-blocking */ }
  }, []);
  useEffect(() => {
    if (!user) return;
    fetchProjects();
  }, [user, fetchProjects]);
  // Refetch when coming back from the Projects master page (so newly added items appear immediately)
  useEffect(() => {
    if (user && location.pathname !== '/projects') fetchProjects();
  }, [location.pathname, user, fetchProjects]);

  // Projects hidden from the sidebar at the customer's request (Oct 2026). Their records and centres are kept.
  const HIDDEN_SIDEBAR_PROJECTS = ['MUDA', 'NABARD', 'SCHOW'];

  const isAdminOrChecker = () => user?.role === 'Admin' || user?.role === 'Checker';
  const isCRPEP = () => user?.role === 'CRP-EP';

  // CRP-EP role: Business Plans + SVEP (so they can enrol new SVEP entrepreneurs themselves)
  const navigation = isCRPEP() ? [
    { name: 'SVEP', path: '/entrepreneurs', icon: Briefcase },
    { name: 'Business Plans', path: '/business-plans', icon: ClipboardList },
  ] : [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },                    // 1
    {
      name: 'Projects',
      icon: FolderKanban,
      isGroup: true,
      // Admin gets a "+" master shortcut in the group header (see rendering below)
      masterPath: isAdminOnly() ? '/projects' : null,
      children: [
        { name: 'SVEP', path: '/entrepreneurs', icon: Briefcase },
        // Dynamic project names created in the Project Master
        ...projects.filter((p) => !HIDDEN_SIDEBAR_PROJECTS.includes((p.name || '').trim().toUpperCase())).map((p) => ({
          name: p.name,
          path: `/entrepreneurs?project=${p.id}`,
          projectId: p.id,
          icon: Folder,
        })),
      ],
    },
    { name: 'Staff', path: '/staff', icon: Users },                             // 2
    { name: 'Business Plans', path: '/business-plans', icon: ClipboardList },   // Step 2: Business Plan
    ...(isAdminOrChecker() ? [{ name: 'Branches', path: '/branches', icon: Building2 }] : []), // 4
    { name: 'Loans', path: '/loans', icon: Banknote },                          // 5
    { name: 'Expenditures', path: '/expenditures', icon: Receipt },             // 6
    { name: 'Branch Ledger', path: '/branch-ledger', icon: Wallet },             // 6b - Branch Income & Expenditure
    ...(isAdminOnly() ? [{ name: 'Member Ledger', path: '/member-ledger', icon: BookOpen }] : []), // Admin only
    { name: 'Funds', path: '/funds', icon: Wallet },                            // 7
    { name: 'Cash Transfers', path: '/cash-transfers', icon: ArrowRightLeft },   // 7b - bank ↔ cash
    { name: 'Assets', path: '/assets', icon: Package },                         // 8
    { name: 'Livelihoods Cell', path: '/livelihoods', icon: Sprout },  // Visible to all — branch users see only their allowed branches
    { name: 'Resource Persons', path: '/resource-persons', icon: GraduationCap },  // Trainers/RPs
    { name: 'Notices', path: '/notices', icon: Megaphone },                        // HQ broadcasts
    ...(isAdminOrChecker() ? [{ name: 'User Management', path: '/users', icon: UserCog }] : []), // 9
    { name: 'Reports', path: '/reports', icon: FileBarChart },                   // 10
    { name: 'Requisitions', path: '/requisitions', icon: Send, badgeKey: 'requisitions' },                 // Reimbursements
    ...(isAdminOnly() ? [{ name: 'Loan Masters', path: '/loan-masters', icon: Settings }] : []), // Admin only
    ...(isAdminOrChecker() ? [{ name: 'Activity Logs', path: '/activity-logs', icon: Activity }] : []), // 12
    ...(isAdminOnly() ? [{ name: 'Invoices', path: '/invoices', icon: FileText }] : []), // Admin only — bill generation
    ...(isAdminOnly() ? [{ name: 'Backup & Restore', path: '/backup-restore', icon: HardDrive }] : []), // Admin only
    ...(isAdminOnly() ? [{ name: 'Financial Years', path: '/financial-years', icon: Calendar }] : []), // Admin only
    { name: 'Credit History', path: '/reports/credit-history', icon: CreditCard },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/reports') {
      return location.pathname === '/reports';
    }
    // Entrepreneurs family: distinguish SVEP (no query) vs specific project (?project=<id>)
    if (path === '/entrepreneurs' || path.startsWith('/entrepreneurs?')) {
      if (location.pathname !== '/entrepreneurs') return false;
      const currentProject = new URLSearchParams(location.search).get('project');
      if (path === '/entrepreneurs') return !currentProject;
      const linkProject = new URLSearchParams(path.split('?')[1] || '').get('project');
      return currentProject === linkProject;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4">
        <div>
          <h1 className="text-lg font-bold text-emerald-700">WWAGS MIS</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/search"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Search className="w-5 h-5 text-slate-600" />
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — soft emerald gradient panel to distinguish it from the main content */}
      <aside
        className={`
          ${isMobile 
            ? `fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
            : `${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300`
          }
          bg-gradient-to-b from-emerald-100 via-teal-50 to-emerald-100 border-r border-emerald-300 shadow-inner flex flex-col no-print
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-emerald-200 bg-white/60 backdrop-blur">
          {(sidebarOpen || isMobile) && (
            <div>
              <h1 className="text-xl font-bold text-emerald-700" data-testid="app-title">WWAGS MIS</h1>
              <p className="text-xs text-emerald-800/70">BRC Portal</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {!isMobile && (
              <Link
                to="/search"
                className="p-2 hover:bg-emerald-100 rounded-lg transition-colors"
                data-testid="search-link"
              >
                <Search className="w-5 h-5 text-emerald-700" />
              </Link>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-emerald-100 rounded-lg transition-colors"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5 text-emerald-700" /> : <Menu className="w-5 h-5 text-emerald-700" />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;

            // Grouped (expandable) item — e.g., Projects → [Projects, SVEP]
            if (item.isGroup) {
              if (!item.children || item.children.length === 0) return null;
              const expanded = !!expandedGroups[item.name];
              const anyChildActive = item.children.some((c) => isActive(c.path));
              const collapsedSidebar = !sidebarOpen && !isMobile;

              // When sidebar is collapsed (desktop), render children as flat icons (no group header)
              if (collapsedSidebar) {
                return (
                  <div key={`group-${item.name}`} className="space-y-1">
                    {item.children.map((child) => {
                      const CIcon = child.icon;
                      const cactive = isActive(child.path);
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                            cactive
                              ? 'bg-emerald-600 text-white shadow-sm font-medium'
                              : 'text-slate-700 hover:bg-emerald-100 hover:text-emerald-800'
                          }`}
                        >
                          <CIcon className="w-5 h-5" strokeWidth={cactive ? 2 : 1.5} />
                        </Link>
                      );
                    })}
                  </div>
                );
              }

              return (
                <div key={`group-${item.name}`} className="space-y-1">
                  <div
                    className={`w-full flex items-center gap-2 rounded-lg transition-all ${
                      anyChildActive
                        ? 'bg-emerald-200/70 text-emerald-900 font-medium'
                        : 'text-slate-700 hover:bg-emerald-100/70 hover:text-emerald-800'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroup(item.name)}
                      data-testid={`nav-group-${item.name.toLowerCase()}`}
                      className="flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-left"
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={anyChildActive ? 2 : 1.5} />
                      <span className="text-sm flex-1">{item.name}</span>
                      {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    {item.masterPath && (
                      <Link
                        to={item.masterPath}
                        title={`Manage ${item.name}`}
                        data-testid={`nav-group-${item.name.toLowerCase()}-master`}
                        className="mr-1 p-1.5 rounded-md hover:bg-emerald-300/60 text-emerald-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Plus className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                  {expanded && (
                    <div className="ml-3 pl-3 border-l border-emerald-300/60 space-y-1">
                      {item.children.map((child) => {
                        const CIcon = child.icon;
                        const cactive = isActive(child.path);
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                              cactive
                                ? 'bg-emerald-600 text-white shadow-sm font-medium'
                                : 'text-slate-700 hover:bg-emerald-100 hover:text-emerald-800'
                            }`}
                          >
                            <CIcon className="w-4 h-4 flex-shrink-0" strokeWidth={cactive ? 2 : 1.5} />
                            <span className="text-sm">{child.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const active = isActive(item.path);
            const badgeCount = item.badgeKey ? (notifCounts[item.badgeKey] || 0) : 0;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  active
                    ? 'bg-emerald-600 text-white shadow-sm font-medium'
                    : 'text-slate-700 hover:bg-emerald-100 hover:text-emerald-800'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <Icon className="w-5 h-5" strokeWidth={active ? 2 : 1.5} />
                  {badgeCount > 0 && !active && !sidebarOpen && !isMobile && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center bg-red-600 text-white text-[10px] font-bold rounded-full px-1" data-testid={`badge-${item.badgeKey}`}>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </div>
                {(sidebarOpen || isMobile) && (
                  <span className="text-sm flex-1 flex items-center justify-between">
                    {item.name}
                    {badgeCount > 0 && !active && (
                      <span className="min-w-[20px] h-5 flex items-center justify-center bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5" data-testid={`badge-${item.badgeKey}`}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-emerald-200 bg-white/70 backdrop-blur">
          {(sidebarOpen || isMobile) ? (
            <div className="space-y-2">
              <div className="text-sm">
                <p className="font-medium text-slate-900 truncate">{user?.branch_name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mt-1 ${
                  user?.role === 'Admin' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {user?.role}
                </span>
              </div>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={logout}
              data-testid="logout-button-collapsed"
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 overflow-auto flex flex-col ${isMobile ? 'pt-14' : ''}`}>
        <div className="flex-1">
          <Outlet />
        </div>
        
        {/* Copyright Footer */}
        <footer className="py-3 px-4 text-center text-xs text-slate-500 border-t border-slate-200 bg-white" data-testid="copyright-footer">
          MIS Developed by Institute of Skill Development and Entrepreneurship (ISDE), Manipur
          <br />
          (An Institute run under Shri Suvakumar Singh Memorial Trust, SSSMT)
        </footer>
      </main>

      {/* HQ Notice Popup (auto-shows unacknowledged notices after login) */}
      <NoticePopup />
      {/* Route transition loader (centered red spinner) */}
      <RouteLoader />
    </div>
  );
};

export default Layout;